import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  Button,
  Input,
  Textarea,
  Select,
  Switch,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  HStack,
  Heading,
  Avatar,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FaUser, FaSave, FaLock, FaBell, FaShieldAlt, FaHistory } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  getProfile,
  updateProfile,
  changePassword,
  getMyActivities
} from '../services/profileService';

function Profile() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  const toastHook = useToast();
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  
  // React Hook Form برای فرم‌ها
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm();

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch: watchPassword,
    setValue: setPasswordValue,
  } = useForm();

  // State برای تنظیمات
  const [profileSettings, setProfileSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    appointmentReminders: true,
    systemUpdates: false,
    privacyMode: false,
    showOnlineStatus: true
  });

  // دریافت اطلاعات پروفایل از سرور
  const {
    data: profileData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['profile'],
    getProfile,
    {
      select: res => res.data?.data?.user || res.data?.user || res.data, // پشتیبانی از ساختارهای مختلف پاسخ
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );

  // دریافت تاریخچه فعالیت‌ها
  const {
    data: activityHistoryData,
    isLoading: isLoadingActivity
  } = useQuery(
    ['myActivities'],
    () => getMyActivities(20),
    { 
      enabled: true, // همیشه فعال باشد
      select: res => res.data?.data || res.data 
    }
  );

  // Mutation برای بروزرسانی پروفایل
  const updateProfileMutation = useMutation(
    updateProfile,
    {
      onSuccess: () => {
        toast.success('پروفایل با موفقیت بروزرسانی شد');
        queryClient.invalidateQueries(['profile']);
      },
      onError: (error) => {
        toast.error(error.message || 'خطا در بروزرسانی پروفایل');
      }
    }
  );

  // Mutation برای تغییر رمز عبور
  const changePasswordMutation = useMutation(
    changePassword,
    {
      onSuccess: () => {
        toast.success('رمز عبور با موفقیت تغییر یافت');
        resetPassword();
      },
      onError: (error) => {
        toast.error(error.message || 'خطا در تغییر رمز عبور');
      }
    }
  );

  // تابع بروزرسانی پروفایل
  const handleUpdateProfile = (data) => {
    updateProfileMutation.mutate(data);
  };

  // تابع تغییر رمز عبور
  const handleChangePassword = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('رمز عبور جدید و تکرار آن مطابقت ندارند');
      return;
    }
    changePasswordMutation.mutate(data);
  };

  // پر کردن فرم‌ها با داده‌های دریافتی از سرور
  React.useEffect(() => {
    if (profileData) {
      // پر کردن فرم اطلاعات شخصی
      setValue('name', profileData.name || '');
      setValue('email', profileData.email || '');
      setValue('phone', profileData.phone || '');
      setValue('specialty', profileData.specialty || '');
      setValue('licenseNumber', profileData.licenseNumber || '');
      setValue('experience', profileData.experience || '');
      setValue('education', profileData.education || '');
      setValue('bio', profileData.bio || '');
      setValue('hourlyRate', profileData.hourlyRate || 0);
      setValue('sessionDuration', profileData.sessionDuration || 60);
    }
  }, [profileData, setValue]);

  const profile = profileData;

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری پروفایل...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری پروفایل!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: "12px", md: "7px" }} dir="rtl">
      <Box mx="auto" p={{ base: "5px", md: "10px" }} pe="20px" minH="100vh">
        <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
          <Box p="20px 0px 35px 0px">
            <Flex direction="column">
              <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                پروفایل کاربری
              </Text>
              <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                مدیریت اطلاعات شخصی و تنظیمات حساب کاربری
              </Text>
            </Flex>
          </Box>

          <Tabs variant="enclosed" colorScheme="teal" px="25px">
            <TabList>
              <Tab>
                <HStack spacing={2}>
                  <FaUser />
                  <Text>اطلاعات شخصی</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <FaLock />
                  <Text>امنیت</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <FaHistory />
                  <Text>فعالیت‌ها</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* اطلاعات شخصی */}
              <TabPanel>
                <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
                  <GridItem>
                    <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
                      <Box mb="20px">
                        <HStack spacing={3}>
                          <FaUser color="teal" />
                          <Heading size="md" color={textColor}>اطلاعات شخصی</Heading>
                        </HStack>
                      </Box>
                      <Box>
                        <form onSubmit={handleSubmit(handleUpdateProfile)}>
                          <VStack spacing={4} align="stretch">
                            <FormControl isInvalid={errors.name}>
                              <FormLabel>نام و نام خانوادگی</FormLabel>
                              <Input
                                {...register('name', { required: 'نام الزامی است' })}
                                placeholder="نام و نام خانوادگی"
                              />
                              <FormErrorMessage>
                                {errors.name && errors.name.message}
                              </FormErrorMessage>
                            </FormControl>

                                                          <FormControl isInvalid={errors.email}>
                                <FormLabel>ایمیل</FormLabel>
                                <Input
                                  {...register('email', { 
                                    pattern: {
                                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                      message: 'ایمیل نامعتبر است'
                                    }
                                  })}
                                  placeholder="ایمیل"
                                  type="email"
                                />
                                <FormErrorMessage>
                                  {errors.email && errors.email.message}
                                </FormErrorMessage>
                              </FormControl>

                            <FormControl isInvalid={errors.phone}>
                              <FormLabel>شماره تماس</FormLabel>
                              <Input
                                {...register('phone')}
                                placeholder="شماره تماس"
                              />
                              <FormErrorMessage>
                                {errors.phone && errors.phone.message}
                              </FormErrorMessage>
                            </FormControl>

                            {user?.role === 'consultant' && (
                              <>
                                <FormControl>
                                  <FormLabel>تخصص</FormLabel>
                                  <Input
                                    {...register('specialty')}
                                    placeholder="تخصص خود را وارد کنید"
                                  />
                                </FormControl>

                                <FormControl>
                                  <FormLabel>شماره پروانه</FormLabel>
                                  <Input
                                    {...register('licenseNumber')}
                                    placeholder="شماره پروانه"
                                  />
                                </FormControl>

                                <FormControl>
                                  <FormLabel>تجربه کاری</FormLabel>
                                  <Input
                                    {...register('experience')}
                                    placeholder="تجربه کاری"
                                  />
                                </FormControl>

                                <FormControl>
                                  <FormLabel>تحصیلات</FormLabel>
                                  <Input
                                    {...register('education')}
                                    placeholder="تحصیلات"
                                  />
                                </FormControl>
                              </>
                            )}

                            {user?.role === 'consultant' && (
                              <FormControl>
                                <FormLabel>درباره من</FormLabel>
                                <Textarea
                                  {...register('bio')}
                                  placeholder="درباره خودتان بنویسید"
                                  rows={4}
                                />
                              </FormControl>
                            )}

                            {user?.role === 'consultant' && (
                              <>
                                <FormControl>
                                  <FormLabel>نرخ ساعتی (تومان)</FormLabel>
                                  <Input
                                    {...register('hourlyRate', { 
                                      valueAsNumber: true,
                                      min: { value: 0, message: 'نرخ ساعتی نمی‌تواند منفی باشد' }
                                    })}
                                    placeholder="نرخ ساعتی"
                                    type="number"
                                  />
                                </FormControl>

                                <FormControl>
                                  <FormLabel>طول هر جلسه (دقیقه)</FormLabel>
                                  <Input
                                    {...register('sessionDuration', { 
                                      valueAsNumber: true,
                                      min: { value: 15, message: 'حداقل طول جلسه 15 دقیقه است' },
                                      max: { value: 180, message: 'حداکثر طول جلسه 180 دقیقه است' }
                                    })}
                                    placeholder="60"
                                    type="number"
                                  />
                                </FormControl>
                              </>
                            )}

                            <Button
                              leftIcon={<FaSave />}
                              colorScheme="teal"
                              type="submit"
                              isLoading={updateProfileMutation.isLoading}
                            >
                              بروزرسانی پروفایل
                            </Button>
                          </VStack>
                        </form>
                      </Box>
                    </Box>
                  </GridItem>

                  <GridItem>
                    <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
                      <Box mb="20px">
                        <Heading size="md" color={textColor}>اطلاعات حساب</Heading>
                      </Box>
                      <Box>
                        <VStack spacing={4} align="stretch">
                          <Flex align="center" justify="space-between">
                            <Text fontWeight="bold">نقش:</Text>
                            <Badge colorScheme="teal" fontSize="md">
                              {user?.role === 'admin' ? 'مدیر' : user?.role === 'consultant' ? 'مشاور' : 'مراجع'}
                            </Badge>
                          </Flex>

                          <Flex align="center" justify="space-between">
                            <Text fontWeight="bold">وضعیت:</Text>
                            <Badge colorScheme="green" fontSize="md">
                              فعال
                            </Badge>
                          </Flex>

                          <Flex align="center" justify="space-between">
                            <Text fontWeight="bold">تاریخ عضویت:</Text>
                            <Text>{profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('fa-IR') : 'نامشخص'}</Text>
                          </Flex>

                          <Flex align="center" justify="space-between">
                            <Text fontWeight="bold">آخرین بروزرسانی:</Text>
                            <Text>{profile?.updatedAt ? new Date(profile.updatedAt).toLocaleDateString('fa-IR') : 'نامشخص'}</Text>
                          </Flex>

                          <Flex align="center" justify="space-between">
                            <Text fontWeight="bold">وضعیت فعال:</Text>
                            <Badge colorScheme={profile?.isActive ? "green" : "red"} fontSize="md">
                              {profile?.isActive ? 'فعال' : 'غیرفعال'}
                            </Badge>
                          </Flex>

                          <Divider />

                          <Box textAlign="center">
                            <Avatar
                              size="xl"
                              name={profile?.name || 'کاربر'}
                              bg="teal.500"
                              mb={3}
                            />
                            <Text fontSize="lg" fontWeight="bold">
                              {profile?.name || 'کاربر'}
                            </Text>
                            <Text color="gray.500">{profile?.email || 'ایمیل نامشخص'}</Text>
                            {user?.role === 'consultant' && profile?.specialty && (
                              <Text color="gray.400" fontSize="sm">
                                تخصص: {profile.specialty}
                              </Text>
                            )}
                          </Box>
                        </VStack>
                      </Box>
                    </Box>
                  </GridItem>
                </Grid>
              </TabPanel>

              {/* امنیت */}
              <TabPanel>
                <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
                  <GridItem>
                    <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
                      <Box mb="20px">
                        <HStack spacing={3}>
                          <FaLock color="red" />
                          <Heading size="md" color={textColor}>تغییر رمز عبور</Heading>
                        </HStack>
                      </Box>
                      <Box>
                        <form onSubmit={handleSubmitPassword(handleChangePassword)}>
                          <VStack spacing={4} align="stretch">
                            <FormControl isInvalid={passwordErrors.currentPassword}>
                              <FormLabel>رمز عبور فعلی</FormLabel>
                              <Input
                                {...registerPassword('currentPassword', { required: 'رمز عبور فعلی الزامی است' })}
                                type="password"
                                placeholder="رمز عبور فعلی"
                              />
                              <FormErrorMessage>
                                {passwordErrors.currentPassword && passwordErrors.currentPassword.message}
                              </FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={passwordErrors.newPassword}>
                              <FormLabel>رمز عبور جدید</FormLabel>
                              <Input
                                {...registerPassword('newPassword', { 
                                  required: 'رمز عبور جدید الزامی است',
                                  minLength: { value: 8, message: 'رمز عبور باید حداقل 8 کاراکتر باشد' }
                                })}
                                type="password"
                                placeholder="رمز عبور جدید"
                              />
                              <FormErrorMessage>
                                {passwordErrors.newPassword && passwordErrors.newPassword.message}
                              </FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={passwordErrors.confirmPassword}>
                              <FormLabel>تکرار رمز عبور جدید</FormLabel>
                              <Input
                                {...registerPassword('confirmPassword', { 
                                  required: 'تکرار رمز عبور الزامی است',
                                  validate: value => value === watchPassword('newPassword') || 'رمز عبور مطابقت ندارد'
                                })}
                                type="password"
                                placeholder="تکرار رمز عبور جدید"
                              />
                              <FormErrorMessage>
                                {passwordErrors.confirmPassword && passwordErrors.confirmPassword.message}
                              </FormErrorMessage>
                            </FormControl>

                            <Button
                              leftIcon={<FaLock />}
                              colorScheme="red"
                              type="submit"
                              isLoading={changePasswordMutation.isLoading}
                            >
                              تغییر رمز عبور
                            </Button>
                          </VStack>
                        </form>
                      </Box>
                    </Box>
                  </GridItem>

                  <GridItem>
                    <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
                      <Box mb="20px">
                        <HStack spacing={3}>
                          <FaShieldAlt color="orange" />
                          <Heading size="md" color={textColor}>تنظیمات امنیت</Heading>
                        </HStack>
                      </Box>
                      <Box>
                        <VStack spacing={4} align="stretch">
                          <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0">احراز هویت دو مرحله‌ای</FormLabel>
                            <Switch colorScheme="red" />
                          </FormControl>

                          <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0">نمایش وضعیت آنلاین</FormLabel>
                            <Switch colorScheme="teal" defaultChecked />
                          </FormControl>

                          <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0">حالت حریم خصوصی</FormLabel>
                            <Switch colorScheme="purple" />
                          </FormControl>

                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <AlertTitle>نکات امنیتی!</AlertTitle>
                              <AlertDescription>
                                برای حفظ امنیت حساب کاربری، رمز عبور قوی انتخاب کنید و احراز هویت دو مرحله‌ای را فعال کنید.
                              </AlertDescription>
                            </Box>
                          </Alert>
                        </VStack>
                      </Box>
                    </Box>
                  </GridItem>
                </Grid>
              </TabPanel>



              {/* فعالیت‌ها */}
              <TabPanel>
                <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
                  <Box mb="20px">
                    <HStack spacing={3}>
                      <FaHistory color="green" />
                      <Heading size="md" color={textColor}>تاریخچه فعالیت‌ها</Heading>
                    </HStack>
                  </Box>
                  <Box>
                    <VStack spacing={3} align="stretch">
                      {isLoadingActivity ? (
                        <Flex justify="center" align="center" p={4}>
                          <Spinner size="sm" color="teal.500" />
                          <Text mr={2} fontSize="sm">در حال بارگذاری فعالیت‌ها...</Text>
                        </Flex>
                      ) : activityHistoryData && activityHistoryData.length > 0 ? (
                        activityHistoryData.map((activity, index) => (
                          <Flex key={index} justify="space-between" align="center" p={3} bg="gray.50" borderRadius="md">
                            <HStack spacing={3}>
                              <FaHistory color="teal" />
                              <Text fontSize="sm">{activity.action || activity.description || 'فعالیت'}</Text>
                            </HStack>
                            <VStack spacing={1} align="end">
                              <Text fontSize="xs" color="gray.500">
                                {new Date(activity.createdAt || activity.date).toLocaleDateString('fa-IR')}
                              </Text>
                              {activity.ip && (
                                <Text fontSize="xs" color="gray.400">
                                  IP: {activity.ip}
                                </Text>
                              )}
                            </VStack>
                          </Flex>
                        ))
                      ) : (
                        <Text textAlign="center" color="gray.500" p={4}>
                          هیچ فعالیتی یافت نشد
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Flex>
  );
}

export default Profile; 